import React, { useState, useMemo } from 'react';
import { 
  Calculator, 
  Droplet, 
  Droplets, 
  Copy, 
  Check, 
  AlertTriangle, 
  Info, 
  Sparkles, 
  RotateCcw, 
  Save, 
  Plus, 
  DollarSign, 
  HelpCircle, 
  Layers, 
  BookmarkCheck,
  CheckCircle2,
  FileText
} from 'lucide-react';
import SyringeGraphic from './SyringeGraphic';

const RECONSTITUTION_AGENTS = [
  { id: 'bac', name: 'Bacteriostatic Water (0.9% Benzyl Alcohol)', short: 'BAC Water', desc: 'Standard solvent for multi-dose peptide preservation up to 28-30 days refrigerated.' },
  { id: 'sterile', name: 'Sterile Water for Injection', short: 'Sterile Water', desc: 'Preservative-free. Must be used immediately or discarded.' },
  { id: 'acetic', name: 'Acetic Acid (0.6% Solution)', short: '0.6% Acetic Acid', desc: 'Recommended for fragile or hydrophobic peptides like IGF-1 LR3.' },
  { id: 'saline', name: 'Sodium Chloride 0.9% (Normal Saline)', short: 'Normal Saline', desc: 'Isotonic carrier for specific clinical preparations.' },
  { id: 'custom', name: 'Custom Reconstitution Solvent', short: 'Custom Solvent', desc: 'User specified laboratory or proprietary solvent.' }
];

const VIAL_SIZES = [
  { value: 2, label: '2.0 mL Vial' },
  { value: 3, label: '3.0 mL (Standard Peptide Vial)' },
  { value: 5, label: '5.0 mL Vial' },
  { value: 10, label: '10.0 mL Multi-Dose Vial' }
];

const SYRINGE_OPTIONS = [
  { value: 100, label: '1.0 mL (100 Units)', sub: 'Standard U-100' },
  { value: 50, label: '0.5 mL (50 Units)', sub: 'Precision 0.5 mL' },
  { value: 30, label: '0.3 mL (30 Units)', sub: 'Micro 0.3 mL' }
];

export default function ReconstitutionCalculator({ 
  protocols = [], 
  onSaveToProtocol = null,
  onOpenAddModalWithData = null 
}) {
  // Input states
  const [peptideName, setPeptideName] = useState('Tirzepatide');
  const [selectedProtocolId, setSelectedProtocolId] = useState('');
  
  // Peptide Mass in Vial
  const [massAmount, setMassAmount] = useState('10');
  const [massUnit, setMassUnit] = useState('mg'); // 'mcg' | 'mg' | 'g'

  // Vial Size (Physical Capacity)
  const [vialSize, setVialSize] = useState('3.0');

  // Reconstitution Fluid
  const [agentId, setAgentId] = useState('bac');
  const [waterVolume, setWaterVolume] = useState('2.0'); // in mL

  // Desired Dose
  const [doseAmount, setDoseAmount] = useState('2.5'); // Default to 2.5 mg
  const [doseUnit, setDoseUnit] = useState('mg'); // 'mg' | 'mcg'

  // Syringe Model
  const [syringeCapacity, setSyringeCapacity] = useState(100); // 100 units default

  // Vial Cost (Optional)
  const [vialCost, setVialCost] = useState('');

  // UI state
  const [copied, setCopied] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Available peptide/hormone/AAS protocols from user stack
  const peptideProtocols = useMemo(() => {
    return protocols.filter(p => p.category === 'Peptide' || p.category === 'Hormone' || p.category === 'Hormones & AAS' || p.administrationFormat?.includes('Injection'));
  }, [protocols]);

  const [iuRatio, setIuRatio] = useState(3.0); // 1 mg = 3.0 IU (HGH / Somatropin standard)

  // Load from Protocol selection
  const handleSelectProtocol = (e) => {
    const id = e.target.value;
    setSelectedProtocolId(id);
    if (!id) return;

    const proto = protocols.find(p => p.id === id);
    if (proto) {
      setPeptideName(proto.name);
      // Try to parse dosage
      if (proto.dosageMin) {
        if (proto.unit === 'mg') {
          setDoseAmount(proto.dosageMin.toString());
          setDoseUnit('mg');
        } else if (proto.unit === 'mcg' || proto.unit === 'mcg/ea') {
          setDoseAmount(proto.dosageMin.toString());
          setDoseUnit('mcg');
        } else if (proto.unit === 'IU') {
          setDoseAmount(proto.dosageMin.toString());
          setDoseUnit('IU');
        } else {
          setDoseAmount(proto.dosageMin.toString());
        }
      }
    }
  };

  const isVialIU = massUnit === 'IU';
  const isDoseIU = doseUnit === 'IU';
  const isBothIU = isVialIU && isDoseIU;
  const isMixedIU = (isVialIU && !isDoseIU) || (!isVialIU && isDoseIU);

  const rawMassNum = parseFloat(massAmount) || 0;
  const rawDoseNum = parseFloat(doseAmount) || 0;

  // Convert mass to base micrograms (mcg)
  const totalPeptideMcg = useMemo(() => {
    if (rawMassNum <= 0) return 0;
    if (massUnit === 'g') return rawMassNum * 1000000;
    if (massUnit === 'mg') return rawMassNum * 1000;
    if (massUnit === 'mcg') return rawMassNum;
    // If IU, convert to mcg via iuRatio (1 mg = iuRatio IU => 1 IU = 1000 / iuRatio mcg)
    return (rawMassNum / iuRatio) * 1000;
  }, [rawMassNum, massUnit, iuRatio]);

  // Total mass in mg
  const totalPeptideMg = totalPeptideMcg / 1000;

  // Total vial IU
  const totalVialIU = useMemo(() => {
    if (rawMassNum <= 0) return 0;
    if (massUnit === 'IU') return rawMassNum;
    return totalPeptideMg * iuRatio;
  }, [rawMassNum, massUnit, totalPeptideMg, iuRatio]);

  // Fluid volume in mL
  const fluidVolumeMl = useMemo(() => {
    const num = parseFloat(waterVolume);
    return isNaN(num) || num <= 0 ? 0 : num;
  }, [waterVolume]);

  // Vial physical size in mL
  const vialCapacityMl = useMemo(() => {
    const num = parseFloat(vialSize);
    return isNaN(num) || num <= 0 ? 0 : num;
  }, [vialSize]);

  // Convert desired dose to base micrograms (mcg)
  const desiredDoseMcg = useMemo(() => {
    if (rawDoseNum <= 0) return 0;
    if (doseUnit === 'mg') return rawDoseNum * 1000;
    if (doseUnit === 'mcg') return rawDoseNum;
    // If IU, convert to mcg via iuRatio
    return (rawDoseNum / iuRatio) * 1000;
  }, [rawDoseNum, doseUnit, iuRatio]);

  // Desired dose in IU
  const desiredDoseIU = useMemo(() => {
    if (rawDoseNum <= 0) return 0;
    if (doseUnit === 'IU') return rawDoseNum;
    return (desiredDoseMcg / 1000) * iuRatio;
  }, [rawDoseNum, doseUnit, desiredDoseMcg, iuRatio]);

  // Calculations
  const calculations = useMemo(() => {
    if (rawMassNum <= 0 || fluidVolumeMl <= 0) {
      return {
        concMgMl: 0,
        concMcgMl: 0,
        concIUPerMl: 0,
        mcgPerUnit: 0,
        mgPerUnit: 0,
        yieldPerUnitIU: 0,
        targetUnits: 0,
        drawVolumeMl: 0,
        totalDoses: 0,
        costPerDose: null,
        isCapacityWarning: false,
        isSyringeOverflow: false
      };
    }

    // Concentration
    const concMcgMl = totalPeptideMcg / fluidVolumeMl;
    const concMgMl = concMcgMl / 1000;
    const concIUPerMl = totalVialIU / fluidVolumeMl;

    // In a U-100 syringe: 1 mL = 100 units => 1 unit = 0.01 mL
    const mcgPerUnit = concMcgMl / 100;
    const mgPerUnit = mcgPerUnit / 1000;
    const yieldPerUnitIU = concIUPerMl / 100;

    // Target Units for desired dose
    let targetUnits = 0;
    if (isBothIU) {
      targetUnits = (yieldPerUnitIU > 0 && rawDoseNum > 0) ? rawDoseNum / yieldPerUnitIU : 0;
    } else {
      targetUnits = (mcgPerUnit > 0 && desiredDoseMcg > 0) ? desiredDoseMcg / mcgPerUnit : 0;
    }
    const drawVolumeMl = targetUnits * 0.01;

    // Total Doses per vial
    let totalDoses = 0;
    if (isBothIU) {
      totalDoses = rawDoseNum > 0 ? rawMassNum / rawDoseNum : 0;
    } else {
      totalDoses = desiredDoseMcg > 0 ? totalPeptideMcg / desiredDoseMcg : 0;
    }

    // Cost calculation
    const costNum = parseFloat(vialCost);
    const costPerDose = (!isNaN(costNum) && costNum > 0 && totalDoses > 0)
      ? costNum / totalDoses
      : null;

    // Safety checks
    const isCapacityWarning = vialCapacityMl > 0 && fluidVolumeMl > vialCapacityMl;
    const isSyringeOverflow = targetUnits > syringeCapacity;

    return {
      concMgMl,
      concMcgMl,
      concIUPerMl,
      mcgPerUnit,
      mgPerUnit,
      yieldPerUnitIU,
      targetUnits,
      drawVolumeMl,
      totalDoses,
      costPerDose,
      isCapacityWarning,
      isSyringeOverflow
    };
  }, [
    rawMassNum, 
    rawDoseNum, 
    totalPeptideMcg, 
    totalVialIU, 
    fluidVolumeMl, 
    desiredDoseMcg, 
    isBothIU, 
    vialCapacityMl, 
    syringeCapacity, 
    vialCost
  ]);

  const selectedAgent = RECONSTITUTION_AGENTS.find(a => a.id === agentId) || RECONSTITUTION_AGENTS[0];

  // Reset to standard values
  const handleReset = () => {
    setPeptideName('Tirzepatide');
    setSelectedProtocolId('');
    setMassAmount('10');
    setMassUnit('mg');
    setVialSize('3.0');
    setAgentId('bac');
    setWaterVolume('2.0');
    setDoseAmount('2.5');
    setDoseUnit('mg');
    setSyringeCapacity(100);
    setVialCost('');
    setSaveSuccessMessage('');
  };

  // Clipboard Copy Recipe
  const handleCopyRecipe = () => {
    const formattedDose = doseUnit === 'IU'
      ? `${parseFloat(doseAmount)} IU`
      : (doseUnit === 'mg' 
          ? `${parseFloat(doseAmount)} mg` 
          : `${parseFloat(doseAmount)} mcg (${(desiredDoseMcg / 1000).toFixed(2)} mg)`);

    const vialQuantityStr = massUnit === 'IU'
      ? `${massAmount} IU (~${(rawMassNum / iuRatio).toFixed(2)} mg)`
      : `${massAmount} ${massUnit} (${totalPeptideMg.toFixed(2)} mg)`;

    const concentrationStr = isBothIU
      ? `${calculations.concIUPerMl.toFixed(1)} IU/mL (${calculations.yieldPerUnitIU.toFixed(2)} IU / unit)`
      : `${calculations.concMgMl.toFixed(2)} mg/mL (${calculations.mcgPerUnit.toFixed(1)} mcg / unit)`;

    const recipe = [
      `🧪 PEPTIDE RECONSTITUTION INSTRUCTIONS`,
      `Compound: ${peptideName || 'Peptide'}`,
      `Vial Quantity: ${vialQuantityStr}`,
      `Reconstitution Solvent: ${selectedAgent.short} (${fluidVolumeMl.toFixed(1)} mL added to ${vialCapacityMl} mL vial)`,
      `Concentration: ${concentrationStr}`,
      `------------------------------------`,
      `🎯 TARGET DOSE: ${formattedDose}`,
      `💉 SYRINGE DRAW: ${calculations.targetUnits.toFixed(1)} UNITS (${calculations.drawVolumeMl.toFixed(3)} mL) on U-${syringeCapacity} Syringe`,
      `------------------------------------`,
      `Yield: ~${calculations.totalDoses.toFixed(1)} doses per vial`,
      calculations.costPerDose ? `Cost: $${calculations.costPerDose.toFixed(2)} per dose` : null,
      `Generated by Supps & Peps Protocol Tracker`
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(recipe);
    setCopied(true);
    setTimeout(() => setCopied(false), 2800);
  };

  // Save to Protocol Notes
  const handleSaveToProtocol = async () => {
    if (!selectedProtocolId || !onSaveToProtocol) return;
    setIsSaving(true);
    try {
      const summaryText = `[Reconstitution: ${massAmount}${massUnit} in ${fluidVolumeMl}mL ${selectedAgent.short} = ${isBothIU ? `${calculations.yieldPerUnitIU.toFixed(2)}IU/unit` : `${calculations.mcgPerUnit.toFixed(1)}mcg/unit`}. Target draw: ${calculations.targetUnits.toFixed(1)} units (${calculations.drawVolumeMl.toFixed(2)}mL) on U-${syringeCapacity} syringe for ${doseAmount}${doseUnit}]`;
      await onSaveToProtocol(selectedProtocolId, summaryText);
      setSaveSuccessMessage('Reconstitution instructions appended to protocol notes!');
      setTimeout(() => setSaveSuccessMessage(''), 4000);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Tool Introduction & Stack Import Header */}
      <div className="bg-slate-900/80 rounded-2xl border border-slate-800 p-5 sm:p-6 shadow-xl backdrop-blur-md">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="p-2 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
                <Droplets className="w-5 h-5" />
              </span>
              <h2 className="text-xl font-bold text-white tracking-tight">
                Peptide Reconstitution Calculator
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                U-100 Calibrated
              </span>
            </div>
            <p className="text-sm text-slate-400 max-w-2xl">
              Calculate dilution ratios, vial concentrations, and target syringe tick marks with an interactive live U-100 syringe graphic and safety volume checks.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center space-x-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium border border-slate-700 transition"
              title="Reset fields to standard defaults"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset</span>
            </button>

            <button
              type="button"
              onClick={handleCopyRecipe}
              className="flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition shadow-sm"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 font-semibold">Copied Recipe!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 text-sky-400" />
                  <span>Copy Recipe</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stack Integration Bar (if user has protocols) */}
        {peptideProtocols.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-800/90 bg-slate-950/60 p-4 rounded-xl border border-sky-500/15 space-y-3">
            <div className="flex items-center space-x-2 text-xs text-slate-300 min-w-0">
              <BookmarkCheck className="w-4 h-4 text-sky-400 shrink-0" />
              <span className="font-medium">Quick load dosage from your saved protocol stack:</span>
            </div>

            <div className="space-y-2">
              <select
                value={selectedProtocolId}
                onChange={handleSelectProtocol}
                aria-label="Select protocol from your stack"
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-sky-500 transition"
              >
                <option value="">-- Choose Protocol --</option>
                {peptideProtocols.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.dosageMin}{p.unit || 'mg'})
                  </option>
                ))}
              </select>

              {selectedProtocolId && onSaveToProtocol && (
                <button
                  type="button"
                  onClick={handleSaveToProtocol}
                  disabled={isSaving}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold text-xs transition disabled:opacity-50 shadow-md shadow-sky-500/20 active:scale-[0.99]"
                  title="Append these reconstitution specs into this protocol's notes in your stack"
                >
                  <Save className="w-4 h-4 shrink-0" />
                  <span>{isSaving ? 'Saving...' : 'Save to Notes'}</span>
                </button>
              )}
            </div>
          </div>
        )}

        {saveSuccessMessage && (
          <div className="mt-3 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-center space-x-2 animate-fadeIn">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span>{saveSuccessMessage}</span>
          </div>
        )}
      </div>

      {/* Main Grid: Calculator Inputs & Syringe Display */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* LEFT COLUMN: Configuration Inputs (5 cols on lg) */}
        <div className="lg:col-span-5 space-y-5">

          {/* Section 1: Peptide Information & Vial Quantity */}
          <div className="bg-slate-900/80 rounded-2xl border border-slate-800 p-5 shadow-lg space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
              <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center space-x-1.5">
                <span className="w-2 h-2 rounded-full bg-sky-400" />
                <span>1. Peptide Compound & Vial Mass</span>
              </h3>
              <span className="text-[11px] text-slate-500 font-mono">Lyophilized Powder</span>
            </div>

            {/* Peptide Name */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Compound Name
              </label>
              <input
                type="text"
                value={peptideName}
                onChange={(e) => setPeptideName(e.target.value)}
                placeholder="e.g. Tirzepatide, BPC-157, Semaglutide"
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
              />
            </div>

            {/* Peptide Mass in Vial */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-400">
                  Total Peptide in Vial
                </label>
                <span className="text-[11px] text-sky-400 font-mono">
                  {massUnit === 'IU' 
                    ? `= ${rawMassNum.toLocaleString()} IU (~${(rawMassNum / iuRatio).toFixed(2)} mg)` 
                    : `= ${totalPeptideMg.toFixed(2)} mg (${totalPeptideMcg.toLocaleString()} mcg)`}
                </span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="number"
                  min="0.001"
                  step="any"
                  value={massAmount}
                  onChange={(e) => setMassAmount(e.target.value)}
                  className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-sm font-mono text-white focus:outline-none focus:border-sky-500 transition"
                />
                <select
                  value={massUnit}
                  onChange={(e) => setMassUnit(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-white focus:outline-none focus:border-sky-500"
                >
                  <option value="mg">mg</option>
                  <option value="mcg">mcg</option>
                  <option value="IU">IU (Units)</option>
                  <option value="g">grams (g)</option>
                </select>
              </div>

              {/* Quick Mass Presets */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-[11px] text-slate-500">Presets:</span>
                {massUnit === 'IU' ? (
                  ['10', '24', '36', '1000', '5000', '10000'].map(val => (
                    <button
                      key={`mass-preset-${val}`}
                      type="button"
                      onClick={() => { setMassAmount(val); setMassUnit('IU'); }}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-mono border transition ${
                        massAmount === val && massUnit === 'IU'
                          ? 'bg-sky-500/20 text-sky-300 border-sky-500/40 font-bold'
                          : 'bg-slate-800/80 hover:bg-slate-800 text-slate-400 border-slate-700/60'
                      }`}
                    >
                      {parseInt(val) >= 1000 ? `${parseInt(val).toLocaleString()} IU` : `${val} IU`}
                    </button>
                  ))
                ) : (
                  ['2', '5', '10', '15', '20', '30'].map(val => (
                    <button
                      key={`mass-preset-${val}`}
                      type="button"
                      onClick={() => { setMassAmount(val); setMassUnit('mg'); }}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-mono border transition ${
                        massAmount === val && massUnit === 'mg'
                          ? 'bg-sky-500/20 text-sky-300 border-sky-500/40 font-bold'
                          : 'bg-slate-800/80 hover:bg-slate-800 text-slate-400 border-slate-700/60'
                      }`}
                    >
                      {val}mg
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Vial Physical Size */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-400 flex items-center space-x-1">
                  <span>Physical Vial Size</span>
                  <span className="text-slate-500" title="Physical bottle capacity to verify fluid fits safely without overpressurizing">
                    <HelpCircle className="w-3.5 h-3.5 inline cursor-help" />
                  </span>
                </label>
                <span className="text-[11px] text-slate-500 font-mono">Max Capacity</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {VIAL_SIZES.map(v => (
                  <button
                    key={`vial-${v.value}`}
                    type="button"
                    onClick={() => setVialSize(v.value.toString())}
                    className={`px-3 py-2 rounded-xl text-left border text-xs transition flex flex-col justify-center ${
                      parseFloat(vialSize) === v.value
                        ? 'bg-sky-500/15 border-sky-500 text-sky-300 font-semibold shadow-sm'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <span className="font-bold text-white">{v.value} mL</span>
                    <span className="text-[10px] text-slate-500 truncate">{v.label.split('(')[1]?.replace(')', '') || 'Vial'}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2: Reconstitution Agent & Liquid Volume */}
          <div className="bg-slate-900/80 rounded-2xl border border-slate-800 p-5 shadow-lg space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
              <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center space-x-1.5">
                <span className="w-2 h-2 rounded-full bg-cyan-400" />
                <span>2. Reconstitution Liquid</span>
              </h3>
              <span className="text-[11px] text-slate-500 font-mono">Diluent</span>
            </div>

            {/* Reconstitution Agent Solvent */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Solvent / Reconstitution Agent
              </label>
              <select
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-xs font-medium text-white focus:outline-none focus:border-cyan-500 transition"
              >
                {RECONSTITUTION_AGENTS.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1.5 italic">
                {selectedAgent.desc}
              </p>
            </div>

            {/* Volume Added */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-400">
                  Volume of Fluid Added
                </label>
                <span className="text-[11px] text-cyan-400 font-mono">
                  {fluidVolumeMl} mL ({fluidVolumeMl * 100} units)
                </span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={waterVolume}
                  onChange={(e) => setWaterVolume(e.target.value)}
                  className={`flex-1 bg-slate-950 border rounded-xl px-3.5 py-2 text-sm font-mono text-white focus:outline-none transition ${
                    calculations.isCapacityWarning 
                      ? 'border-amber-500/80 focus:border-amber-400 text-amber-200' 
                      : 'border-slate-800 focus:border-cyan-500'
                  }`}
                />
                <span className="inline-flex items-center px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs font-semibold text-slate-400">
                  mL
                </span>
              </div>

              {/* Quick Volume Presets */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-[11px] text-slate-500">Presets:</span>
                {['1.0', '1.5', '2.0', '2.5', '3.0', '5.0'].map(val => (
                  <button
                    key={`vol-preset-${val}`}
                    type="button"
                    onClick={() => setWaterVolume(val)}
                    className={`px-2 py-0.5 rounded-md text-[11px] font-mono border transition ${
                      waterVolume === val
                        ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40 font-bold'
                        : 'bg-slate-800/80 hover:bg-slate-800 text-slate-400 border-slate-700/60'
                    }`}
                  >
                    {val}mL
                  </button>
                ))}
              </div>
            </div>

            {/* Capacity safety warning */}
            {calculations.isCapacityWarning && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs space-y-1">
                <div className="flex items-center space-x-2 font-semibold">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span>Vial Capacity Exceeded</span>
                </div>
                <p className="text-[11px] text-amber-200/90 leading-relaxed pl-6">
                  You entered <strong className="font-mono">{fluidVolumeMl} mL</strong> of solvent, which is greater than the stated vial capacity (<strong className="font-mono">{vialCapacityMl} mL</strong>). This creates severe positive pressure or overflow risk. Use a larger vial or less diluent.
                </p>
              </div>
            )}
          </div>

          {/* Section 3: Desired Dose & Syringe Calibration */}
          <div className="bg-slate-900/80 rounded-2xl border border-slate-800 p-5 shadow-lg space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
              <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center space-x-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>3. Desired Dose & Syringe</span>
              </h3>
              <span className="text-[11px] text-slate-500 font-mono">Administration</span>
            </div>

            {/* Desired Dose */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-400">
                  Target Dose per Administration
                </label>
                <span className="text-[11px] text-emerald-400 font-mono">
                  {doseUnit === 'IU'
                    ? `= ${rawDoseNum.toLocaleString()} IU (~${(rawDoseNum / iuRatio).toFixed(3)} mg)`
                    : (doseUnit === 'mg' 
                        ? `= ${rawDoseNum} mg (${(rawDoseNum * 1000).toLocaleString()} mcg)` 
                        : `= ${(rawDoseNum / 1000).toFixed(3)} mg`)}
                </span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="number"
                  min="0.01"
                  step="any"
                  value={doseAmount}
                  onChange={(e) => setDoseAmount(e.target.value)}
                  className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-sm font-mono text-white focus:outline-none focus:border-emerald-500 transition"
                />
                <select
                  value={doseUnit}
                  onChange={(e) => setDoseUnit(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="mg">mg (milligrams)</option>
                  <option value="mcg">mcg (micrograms)</option>
                  <option value="IU">IU (Units)</option>
                </select>
              </div>

              {/* Quick Dose Presets */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-[11px] text-slate-500">Presets:</span>
                {doseUnit === 'IU' ? (
                  [
                    { label: '1 IU', amount: '1' },
                    { label: '2 IU', amount: '2' },
                    { label: '3 IU', amount: '3' },
                    { label: '250 IU', amount: '250' },
                    { label: '500 IU', amount: '500' },
                    { label: '1,000 IU', amount: '1000' }
                  ].map(p => (
                    <button
                      key={`dose-preset-${p.label}`}
                      type="button"
                      onClick={() => { setDoseAmount(p.amount); setDoseUnit('IU'); }}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-mono border transition ${
                        doseAmount === p.amount && doseUnit === 'IU'
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 font-bold'
                          : 'bg-slate-800/80 hover:bg-slate-800 text-slate-400 border-slate-700/60'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))
                ) : (
                  [
                    { label: '250 mcg', amount: '250', unit: 'mcg' },
                    { label: '500 mcg', amount: '500', unit: 'mcg' },
                    { label: '1 mg', amount: '1', unit: 'mg' },
                    { label: '2.5 mg', amount: '2.5', unit: 'mg' },
                    { label: '5 mg', amount: '5', unit: 'mg' },
                    { label: '7.5 mg', amount: '7.5', unit: 'mg' }
                  ].map(p => (
                    <button
                      key={`dose-preset-${p.label}`}
                      type="button"
                      onClick={() => { setDoseAmount(p.amount); setDoseUnit(p.unit); }}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-mono border transition ${
                        doseAmount === p.amount && doseUnit === p.unit
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 font-bold'
                          : 'bg-slate-800/80 hover:bg-slate-800 text-slate-400 border-slate-700/60'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))
                )}
              </div>

              {/* Mixed Units Info Bar (if one is IU and one is mg/mcg) */}
              {isMixedIU && (
                <div className="mt-3 p-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center space-x-1.5">
                    <Info className="w-4 h-4 text-indigo-400 shrink-0" />
                    <span>Mixed Units: <strong>1 mg = {iuRatio} IU</strong> (HGH Somatropin standard)</span>
                  </div>
                  <select
                    value={iuRatio}
                    onChange={(e) => setIuRatio(parseFloat(e.target.value))}
                    className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-white focus:outline-none"
                  >
                    <option value={3.0}>1 mg = 3.0 IU</option>
                    <option value={3.33}>1 mg = 3.33 IU</option>
                  </select>
                </div>
              )}
            </div>

            {/* Syringe Size Selection */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                U-100 Insulin Syringe Size
              </label>
              <div className="grid grid-cols-3 gap-2">
                {SYRINGE_OPTIONS.map(s => (
                  <button
                    key={`syringe-${s.value}`}
                    type="button"
                    onClick={() => setSyringeCapacity(s.value)}
                    className={`p-2.5 rounded-xl border text-center transition flex flex-col items-center justify-center ${
                      syringeCapacity === s.value
                        ? 'bg-sky-500/20 border-sky-400 text-sky-200 font-bold shadow-md shadow-sky-500/10'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <span className="text-xs font-bold text-white">{s.value} Units</span>
                    <span className="text-[10px] text-slate-400 font-mono mt-0.5">{s.label.split('(')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Vial Cost Tracking */}
            <div className="pt-2 border-t border-slate-800">
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Vial Cost (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                  <DollarSign className="w-4 h-4" />
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={vialCost}
                  onChange={(e) => setVialCost(e.target.value)}
                  placeholder="e.g. 45.00"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-2 text-xs font-mono text-white placeholder-slate-600 focus:outline-none focus:border-sky-500"
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Used to compute cost-per-dose and monthly expense breakdown.
              </p>
            </div>

          </div>

        </div>

        {/* RIGHT COLUMN: Results & Dynamic Syringe Display (7 cols on lg) */}
        <div className="lg:col-span-7 space-y-5">

          {/* Primary Metric Highlights Banner */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-sky-950/40 rounded-2xl border border-sky-500/30 p-5 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800/80 pb-4 mb-4">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-sky-400 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  Calculated Target Draw
                </span>
                <div className="flex items-baseline space-x-2 mt-1">
                  <span className="text-4xl sm:text-5xl font-black text-white font-mono tracking-tight">
                    {calculations.targetUnits.toFixed(1)}
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-sky-400 font-mono">
                    Units
                  </span>
                  <span className="text-xs text-slate-400 font-mono pl-2">
                    ({calculations.drawVolumeMl.toFixed(3)} mL)
                  </span>
                </div>
              </div>

              {/* Status pill */}
              <div className="text-right">
                <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-xl bg-slate-800/90 border border-slate-700/80 text-xs">
                  <span className="text-slate-400">Total Doses in Vial:</span>
                  <strong className="text-white font-mono text-sm">~{calculations.totalDoses.toFixed(1)} doses</strong>
                </div>
                {calculations.costPerDose !== null && (
                  <div className="text-[11px] text-emerald-400 font-mono mt-1">
                    ${calculations.costPerDose.toFixed(2)} per injection
                  </div>
                )}
              </div>
            </div>

            {/* 4-Box Key Metrics Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 relative z-10">
              <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/80">
                <span className="block text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  Concentration
                </span>
                <span className="text-sm sm:text-base font-bold text-white font-mono mt-0.5 block truncate">
                  {isBothIU 
                    ? `${calculations.concIUPerMl.toFixed(1)} IU/mL` 
                    : `${calculations.concMgMl.toFixed(2)} mg/mL`}
                </span>
                <span className="text-[10px] text-slate-500 font-mono">
                  {isBothIU 
                    ? `in ${fluidVolumeMl.toFixed(1)} mL` 
                    : (isMixedIU ? `${calculations.concIUPerMl.toFixed(1)} IU/mL` : `${calculations.concMcgMl.toFixed(0)} mcg/mL`)}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/80">
                <span className="block text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  Yield Per Unit
                </span>
                <span className="text-sm sm:text-base font-bold text-cyan-300 font-mono mt-0.5 block truncate">
                  {isBothIU || isDoseIU
                    ? `${calculations.yieldPerUnitIU.toFixed(2)} IU`
                    : `${calculations.mcgPerUnit.toFixed(1)} mcg`}
                </span>
                <span className="text-[10px] text-slate-500 font-mono">
                  {isBothIU || isDoseIU
                    ? `per U-100 unit`
                    : `${(calculations.mgPerUnit).toFixed(3)} mg / unit`}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/80">
                <span className="block text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  Desired Dose
                </span>
                <span className="text-sm sm:text-base font-bold text-emerald-400 font-mono mt-0.5 block truncate">
                  {doseUnit === 'IU'
                    ? `${rawDoseNum.toLocaleString()} IU`
                    : (desiredDoseMcg >= 1000 
                        ? `${(desiredDoseMcg / 1000).toFixed(2)} mg` 
                        : `${desiredDoseMcg.toFixed(0)} mcg`)}
                </span>
                <span className="text-[10px] text-slate-500 font-mono">
                  {doseUnit === 'IU' && isMixedIU
                    ? `~${(desiredDoseMcg / 1000).toFixed(3)} mg`
                    : `${doseAmount} ${doseUnit}`}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/80">
                <span className="block text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  Vial Fill
                </span>
                <span className={`text-sm sm:text-base font-bold font-mono mt-0.5 block truncate ${
                  calculations.isCapacityWarning ? 'text-amber-400' : 'text-slate-200'
                }`}>
                  {fluidVolumeMl.toFixed(1)} / {vialCapacityMl.toFixed(1)} mL
                </span>
                <span className="text-[10px] text-slate-500 font-mono">
                  {vialCapacityMl > 0 ? `${((fluidVolumeMl / vialCapacityMl) * 100).toFixed(0)}% full` : '-'}
                </span>
              </div>
            </div>
          </div>

          {/* DYNAMIC SYRINGE GRAPHIC */}
          <SyringeGraphic
            units={calculations.targetUnits}
            maxUnits={syringeCapacity}
            concentrationMgMl={calculations.concMgMl}
            doseMcg={desiredDoseMcg}
            doseDisplay={
              doseUnit === 'IU'
                ? `${rawDoseNum.toLocaleString()} IU`
                : (desiredDoseMcg >= 1000 ? `${(desiredDoseMcg / 1000).toFixed(2)} mg` : `${desiredDoseMcg.toFixed(0)} mcg`)
            }
            agentName={selectedAgent.short}
            peptideName={peptideName}
          />

          {/* Step-by-Step Protocol Instructions Card */}
          <div className="bg-slate-900/80 rounded-2xl border border-slate-800 p-5 shadow-lg space-y-3.5">
            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center space-x-2">
              <FileText className="w-4 h-4 text-sky-400" />
              <span>Step-by-Step Reconstitution Protocol</span>
            </h3>

            <ol className="space-y-3 text-xs text-slate-300">
              <li className="flex items-start space-x-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-sky-400 font-mono text-[11px] font-bold flex-shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <strong className="text-white">Prepare & Sanitize:</strong> Swab the rubber stoppers of both the lyophilized <strong className="text-white">{peptideName || 'peptide'}</strong> vial ({massAmount}{massUnit}) and the <strong className="text-white">{selectedAgent.short}</strong> bottle with an isopropyl alcohol wipe. Allow to air dry for 30 seconds.
                </div>
              </li>

              <li className="flex items-start space-x-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-sky-400 font-mono text-[11px] font-bold flex-shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  <strong className="text-white">Draw Reconstitution Fluid:</strong> Using a mixing syringe, draw exactly <strong className="text-cyan-300 font-mono">{fluidVolumeMl.toFixed(1)} mL</strong> ({fluidVolumeMl * 100} units) of {selectedAgent.short}.
                </div>
              </li>

              <li className="flex items-start space-x-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-sky-400 font-mono text-[11px] font-bold flex-shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  <strong className="text-white">Gentle Transfer (Crucial):</strong> Angle the needle against the inner glass wall of the peptide vial. Allow the liquid to flow slowly down the side into the powder. <span className="text-amber-300 font-medium">Do not spray directly onto the powder or shake the vial</span>; gently swirl until fully dissolved and clear.
                </div>
              </li>

              <li className="flex items-start space-x-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-sky-400 font-mono text-[11px] font-bold flex-shrink-0 mt-0.5">
                  4
                </span>
                <div>
                  <strong className="text-white">Draw Target Dose:</strong> Using your U-{syringeCapacity} insulin syringe, invert the vial and draw to the <span className="text-red-400 font-bold underline decoration-red-500/80">red target line</span> at <strong className="text-white font-mono">{calculations.targetUnits.toFixed(1)} Units</strong> ({calculations.drawVolumeMl.toFixed(3)} mL) for your <strong className="text-emerald-400 font-mono">{doseAmount} {doseUnit}</strong> dose.
                </div>
              </li>

              <li className="flex items-start space-x-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-sky-400 font-mono text-[11px] font-bold flex-shrink-0 mt-0.5">
                  5
                </span>
                <div>
                  <strong className="text-white">Storage:</strong> Store the reconstituted vial in a dark refrigerator at <strong className="text-white">36°F – 46°F (2°C – 8°C)</strong>. Protect from UV light.
                </div>
              </li>
            </ol>
          </div>

        </div>

      </div>

    </div>
  );
}
